import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useState } from 'react';
import { normalizeExportLabel } from '../core/_utils';
import { type useExportConfig } from '../hooks/useExportConfig';
import ExportColumnRow from './ExportColumnRow';

/** Com catálogo pequeno a busca só ocupa espaço. */
const SEARCH_THRESHOLD = 8;

type Props = { config: ReturnType<typeof useExportConfig> };

/**
 * Lista única e rolável: em cima as colunas do arquivo, na ordem do arquivo; embaixo as que ficaram de fora.
 * No desktop ocupa o que sobra da metade esquerda do modal e só ela rola, com 6 ou com 30 colunas.
 */
const ExportColumnList = ({ config }: Props) => {
  const [search, setSearch] = useState('');
  // Sem sensor de teclado: dentro da lista rolável ele rola antes de mover e Esc fecharia o modal. ↑ ↓ cobrem.
  const sensors = useSensors(useSensor(PointerSensor));

  const query = normalizeExportLabel(search);
  const isSearching = query !== '';
  const matches = (key: string, label: string) =>
    normalizeExportLabel(label).includes(query) || normalizeExportLabel(config.headerOf(key)).includes(query);

  const labels = new Map(config.availableFields.map((field) => [field.key, field.label]));
  const selected = config.selectedKeys.filter((key) => labels.has(key) && matches(key, labels.get(key)!));
  const available = config.availableFields.filter(
    (field) => !config.selectedKeys.includes(field.key) && matches(field.key, field.label),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = config.selectedKeys.indexOf(String(active.id));
    const to = config.selectedKeys.indexOf(String(over.id));
    if (from !== -1 && to !== -1) config.reorderColumns(from, to);
  };

  return (
    <div
      aria-labelledby="export-columns-title"
      className="d-flex flex-column flex-lg-grow-1 overflow-lg-hidden"
      role="group"
    >
      <div className="d-flex align-items-baseline justify-content-between">
        <div className="form-label fw-semibold fs-7 text-gray-600" id="export-columns-title">
          Colunas no arquivo ({config.selectedKeys.length} de {config.availableFields.length})
        </div>
        <div className="d-flex gap-4">
          <button className="btn btn-link btn-sm p-0 fs-8" type="button" onClick={config.selectAllColumns}>
            Todas
          </button>
          <button className="btn btn-link btn-sm p-0 fs-8" type="button" onClick={config.deselectAllColumns}>
            Nenhuma
          </button>
        </div>
      </div>
      {config.availableFields.length > SEARCH_THRESHOLD ? (
        <div className="position-relative mb-3">
          <i className="bi bi-search position-absolute top-50 translate-middle-y ms-4 text-gray-500" />
          <label className="visually-hidden" htmlFor="export-column-search">
            Buscar coluna
          </label>
          <input
            className="form-control form-control-sm form-control-solid ps-10"
            id="export-column-search"
            placeholder="Buscar coluna…"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      ) : null}
      <div className="scroll-y mh-300px mh-lg-unset flex-lg-grow-1 pe-1">
        <DndContext collisionDetection={closestCenter} sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext items={selected} strategy={verticalListSortingStrategy}>
            {selected.map((key) => (
              <ExportColumnRow key={key} config={config} fieldKey={key} reorderDisabled={isSearching} />
            ))}
          </SortableContext>
        </DndContext>
        {available.length > 0 ? (
          <div className="fs-8 fw-bold text-uppercase text-gray-500 mt-4 mb-2">
            Fora do arquivo ({available.length})
          </div>
        ) : null}
        {available.map((field) => (
          <button
            key={field.key}
            aria-label={`Incluir ${field.label}`}
            className="d-flex align-items-center gap-2 w-100 border border-dashed border-gray-300 rounded bg-transparent text-start px-2 py-1 mb-2"
            type="button"
            onClick={() => config.toggleColumn(field.key)}
          >
            <span className="d-flex flex-center w-25px h-25px">
              <i className="bi bi-plus-lg text-gray-500 p-0" />
            </span>
            <span className="flex-grow-1 fs-7 text-gray-600">{field.label}</span>
          </button>
        ))}
        {selected.length + available.length === 0 ? (
          <div className="text-center text-gray-500 fs-7 py-6">Nenhuma coluna com “{search}”.</div>
        ) : null}
      </div>
    </div>
  );
};

export default ExportColumnList;
